import React, { useState } from "react";
import { MultiClassFlightOffer } from "../../types/flight";
import DatePriceTabs from "./DatePriceTabs";
import FlightOfferCard from "./FlightOfferCard";

interface FlightResultsSectionProps {
    origin: string;
    originCode: string;
    destination: string;
    destinationCode: string;
    flights: MultiClassFlightOffer[];
    departureDate: string;
    isOutbound: boolean;
    selectedOffer?: {
        offer: any;
        cabinClass: "economy" | "premiumEconomy" | "business";
        price: number;
        taxes: number;
    } | null;
    onSelectFlight: (
        flight: MultiClassFlightOffer,
        cabinClass: "economy" | "premiumEconomy" | "business",
        price: number,
        fullOffer: any
    ) => void;
}

const FlightResultsSection: React.FC<FlightResultsSectionProps> = ({
    origin,
    originCode,
    destination,
    destinationCode,
    flights,
    departureDate,
    isOutbound,
    selectedOffer,
    onSelectFlight,
}) => {
    const [displayCount, setDisplayCount] = useState(10);
    // Get the lowest price from flights for the date tabs
    const lowestPrice =
        flights.length > 0
            ? Math.min(
                  ...flights
                      .map((f) => {
                          // Get the lowest price from any available cabin class
                          const prices = [];
                          if (f.cabinPrices.economy)
                              prices.push(
                                  parseFloat(f.cabinPrices.economy.price)
                              );
                          if (f.cabinPrices.premiumEconomy)
                              prices.push(
                                  parseFloat(f.cabinPrices.premiumEconomy.price)
                              );
                          if (f.cabinPrices.business)
                              prices.push(
                                  parseFloat(f.cabinPrices.business.price)
                              );
                          return prices.length > 0
                              ? Math.min(...prices)
                              : Infinity;
                      })
                      .filter((p) => p !== Infinity)
              )
            : 400; // default price if no flights

    return (
        <div className="mb-8 -mr-16">
            {/* Section Header */}
            <div className="bg-gray-100 rounded-t-lg py-2 text-2xl text-[#000066]">
                <h2 className="flex items-center gap-1">
                    <span>{origin}</span>
                    <span className="font-bold">{originCode}</span>
                    {/* Airplane icon */}
                    <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        className="w-7 h-7 rotate-90"
                    >
                        <path
                            fill="currentColor"
                            d="M9.144 21.01v-.847l1.885-1.384v-5.866l-8.02 2.385v-1.077l8.02-4.77v-5.48q0-.414.284-.697a.95.95 0 0 1 .697-.284q.413 0 .697.284.283.284.283.697v5.48l8.02 4.77v1.077l-8.02-2.385v5.827l1.885 1.423v.847l-2.865-.847z"
                        ></path>
                    </svg>
                    <span>{destination}</span>
                    <span className="font-bold">{destinationCode}</span>
                </h2>
            </div>

            {/* Date Price Tabs and Sort Dropdown */}
            <div className="flex items-center justify-between mt-4">
                <DatePriceTabs
                    currentDate={departureDate}
                    basePrice={lowestPrice}
                    currency="€"
                />
                <div className="flex items-center">
                    <span className="text-xs text-gray-700">Sort by:</span>
                    <select className="text-sm border-0 px-5 py-1 bg-transparent focus:outline-none underline cursor-pointer text-[#383838] -ml-4">
                        <option value="recommended">Recommended</option>
                        <option value="number_of_stops">Number of stops</option>
                        <option value="duration">Duration</option>
                        <option value="arrival">Arrival time</option>
                        <option value="departure">Departure time</option>
                        <option value="price">Price</option>
                    </select>
                </div>
            </div>

            {/* Outbound label */}
            <div className="bg-[#d9d9d9] px-4 py-2.5 mb-4">
                <span className="text-sm font-semibold text-gray-700 uppercase">
                    {isOutbound ? "Outbound" : "Return"}
                </span>
            </div>

            {/* Flight Cards */}
            <div className="space-y-4">
                {flights.length > 0 ? (
                    flights.slice(0, displayCount).map((flight) => {
                        // Check if this flight contains the selected offer
                        const isSelected = !!(
                            selectedOffer &&
                            ((selectedOffer.cabinClass === "economy" &&
                                flight.cabinPrices.economy?.offer?.id ===
                                    selectedOffer.offer.id) ||
                                (selectedOffer.cabinClass ===
                                    "premiumEconomy" &&
                                    flight.cabinPrices.premiumEconomy?.offer
                                        ?.id === selectedOffer.offer.id) ||
                                (selectedOffer.cabinClass === "business" &&
                                    flight.cabinPrices.business?.offer?.id ===
                                        selectedOffer.offer.id))
                        );

                        return (
                            <FlightOfferCard
                                key={flight.flightKey}
                                flight={flight}
                                isOutbound={isOutbound}
                                isSelected={isSelected}
                                selectedCabinClass={
                                    isSelected
                                        ? selectedOffer.cabinClass
                                        : undefined
                                }
                                onSelect={onSelectFlight}
                            />
                        );
                    })
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No flights available for this route
                    </div>
                )}
            </div>

            {/* Show more link */}
            {flights.length > displayCount && (
                <div className="text-center py-4 border-gray-200">
                    <button
                        className="text-[#003cef] hover:text-blue-800 text-md underline"
                        onClick={() => setDisplayCount((prev) => prev + 10)}
                    >
                        Show more flights (
                        {Math.min(10, flights.length - displayCount)})
                    </button>
                </div>
            )}
        </div>
    );
};

export default FlightResultsSection;
